/**
 * Typed client over the API.
 *
 * `hc<AppType>` derives every path, parameter and response type straight from the
 * Hono app, so there is no generated SDK to regenerate and no hand-maintained
 * response interfaces to drift. Break a handler and this stops compiling.
 */

import type { AppType } from '@ynab/api/types'
import type { BudgetMonth, Milliunits } from '@ynab/core'
import { hc } from 'hono/client'

export interface CreateClientOptions {
  /**
   * API origin. Empty string in the browser, which is the normal case: the web app
   * and the API share the hostname `kete.leereichardt.co.nz`, so requests are
   * same-origin and no base URL is configured anywhere. A native app passes one.
   */
  baseUrl?: string
  /**
   * The current access token, read fresh on every request.
   *
   * A function rather than a string because the token rotates: it expires on the
   * hour and the SDK swaps it underneath. A value captured once at client
   * construction would be valid for exactly as long as the first token, then every
   * request would 401 until the page was reloaded — the kind of failure that looks
   * like the API breaking rather than the client going stale.
   *
   * Returning null is normal and means "not signed in yet"; the header is simply
   * omitted, and the API answers 401, which is what the caller needs to know.
   */
  getToken?: () => string | null | Promise<string | null>
  /** Static headers, for anything that is not the bearer. */
  headers?: Record<string, string>
  fetchImpl?: typeof fetch
}

export function createClient(options: CreateClientOptions = {}) {
  const { getToken, headers } = options
  return hc<AppType>(options.baseUrl ?? '', {
    ...(getToken || headers
      ? {
          headers: async () => {
            const token = getToken ? await getToken() : null
            return {
              ...headers,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            }
          },
        }
      : {}),
    ...(options.fetchImpl ? { fetch: options.fetchImpl } : {}),
  })
}

export type ApiClient = ReturnType<typeof createClient>

interface AnyClientResponse {
  ok: boolean
  status: number
  json: () => Promise<unknown>
}

/**
 * Unwraps a Hono response, turning a non-2xx into a thrown `ApiError`.
 *
 * The success type is supplied by the caller via `InferResponseType<…, 200>`
 * rather than narrowed from `response.ok`. That is not laziness: for a route with
 * a validator, `hc` produces a single `ClientResponse` whose `status` is a union
 * (`200 | 400`), which widens `ok` to `boolean` — so it cannot discriminate the
 * body type. Naming the 200 shape at the call site is the only way to get the
 * success body without Zod's error shape bleeding into it.
 */
export async function unwrap<T>(response: AnyClientResponse): Promise<T> {
  if (!response.ok) {
    let message = `Request failed with ${response.status}`
    try {
      const body = (await response.json()) as { error?: string }
      if (body?.error) message = body.error
    } catch {
      // Keep the status-only message.
    }
    throw new ApiError(message, response.status)
  }
  return (await response.json()) as T
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Re-exported so consumers can name a route's 200 body: `InferResponseType<T, 200>`. */
export type { InferRequestType, InferResponseType } from 'hono/client'
export type { AppType, BudgetMonth, Milliunits }
